import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LuLock } from 'react-icons/lu';

import { AssetClass, AssetInfo } from '@/cip/_openapi/portfolio-optimizer';
import { useSearchAssets } from '@/cip/api/asset';
import { Spinner } from '@/components/Spinner';
import { TickerLogo } from '@/components/TickerLogo';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface ExcludeItem {
    sId: string;
    scId?: string;
    displayText?: string;
}

/**
 * Securities nobody may pick here: SGOV is the cash-equivalent security the engine uses for
 * cash sweeps, so it must not be targeted, restricted, or used as a replacement.
 * Pickers that refer to an existing holding rather than choosing one - billing exclusions -
 * opt out with `allowBlockedSecurities`.
 */
const BLOCKED_SECURITIES: ExcludeItem[] = [
    {
        sId: 'E00002399',
        scId: 'S00002399',
        displayText: 'cash equivalent for sweeps, consider using another ticker e.g. CLIP',
    },
];

const findExcludeItem = (asset: AssetInfo, excludeItems: ExcludeItem[], allowBlockedSecurities: boolean) =>
    [...excludeItems, ...(allowBlockedSecurities ? [] : BLOCKED_SECURITIES)].find(
        (item) => item.sId === asset.sId && item.scId === asset.scId,
    );

interface TickerSelectorInputProps {
    className?: string;
    onSelectTicker: (data: AssetInfo) => void;
    placeholder?: string;
    label?: string;
    value?: string;
    disabled?: boolean;
    usePortal?: boolean;
    autoFocus?: boolean;
    assetClass?: AssetClass;
    excludeItems?: ExcludeItem[];
    /** Let this picker select securities that are otherwise never selectable. */
    allowBlockedSecurities?: boolean;
    excludeAsset?: (asset: AssetInfo) => boolean;
    excludeAssetDisplayText?: string;
}

export const TickerSelectorInput = ({
    className,
    onSelectTicker,
    placeholder = 'Search by ticker or company name...',
    label = 'Enter a Ticker or Company Name',
    value = '',
    disabled = false,
    usePortal = false,
    autoFocus = false,
    assetClass,
    excludeItems = [],
    allowBlockedSecurities = false,
    excludeAsset,
    excludeAssetDisplayText,
}: TickerSelectorInputProps) => {
    const [searchTerm, setSearchTerm] = useState(value);
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(value);
    const [showResults, setShowResults] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
    const inputId = useId();

    useEffect(() => {
        setSearchTerm(value);
        setDebouncedSearchTerm(value);
    }, [value]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 300);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        if (autoFocus && inputRef.current) {
            const timer = setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [autoFocus]);

    const { data: assets = [], isLoading: isSearching } = useSearchAssets(
        debouncedSearchTerm.length >= 2 ? debouncedSearchTerm : undefined,
        10,
        assetClass,
    );

    const updateDropdownPosition = () => {
        if (usePortal && inputRef.current) {
            const rect = inputRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
                width: rect.width,
            });
        }
    };

    const handleSearchChange = (newValue: string) => {
        setSearchTerm(newValue);
        const shouldShow = newValue.length >= 2;
        setShowResults(shouldShow);
        if (shouldShow && usePortal) {
            updateDropdownPosition();
        }
    };

    const handleAssetSelect = (asset: AssetInfo) => {
        const displayValue = `${asset.symbol} - ${asset.description}`;
        setSearchTerm(displayValue);
        setShowResults(false);

        onSelectTicker(asset);
    };

    return (
        <div className="relative space-y-2">
            <Label htmlFor={inputId} className="text-sm font-medium text-gray-700">
                {label}
            </Label>
            <Input
                id={inputId}
                ref={inputRef}
                className={className}
                value={searchTerm}
                autoComplete="off"
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder={placeholder}
                disabled={disabled}
                onFocus={() => {
                    if (searchTerm.length >= 2) {
                        setShowResults(true);
                        if (usePortal) {
                            updateDropdownPosition();
                        }
                    }
                }}
                onBlur={(e) => {
                    setTimeout(() => {
                        if (!e.currentTarget?.contains(document.activeElement)) {
                            setShowResults(false);
                        }
                    }, 150);
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                        setShowResults(false);
                    }
                }}
            />

            {showResults && !usePortal && (
                <div className="border-border absolute top-full right-0 left-0 z-50 max-h-60 overflow-y-auto rounded-md border bg-white shadow-lg">
                    {isSearching && assets.length === 0 ? (
                        <div className="flex items-center justify-center p-4">
                            <Spinner />
                        </div>
                    ) : assets.length > 0 ? (
                        assets.map((asset) => {
                            const excludedItem = findExcludeItem(asset, excludeItems, allowBlockedSecurities);
                            const isAssetExcluded = excludeAsset?.(asset) ?? false;
                            const isExcluded = !!excludedItem || isAssetExcluded;
                            const excludedText =
                                excludedItem?.displayText ?? (isAssetExcluded ? excludeAssetDisplayText : undefined);

                            return (
                                <div
                                    key={asset.sId + '-' + asset.scId}
                                    className={`flex items-center gap-3 border-b border-gray-100 p-3 last:border-b-0 ${
                                        isExcluded ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-gray-50'
                                    }`}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        if (!isExcluded) {
                                            handleAssetSelect(asset);
                                        }
                                    }}
                                >
                                    <TickerLogo
                                        tickerSymbol={asset.symbol || ''}
                                        companyName={asset.description || ''}
                                        className="h-8 w-8 rounded object-cover"
                                        fallbackIconClassName="w-8 h-8"
                                    />
                                    <div className="flex-1 text-left">
                                        <div className="text-sm font-medium">
                                            {asset.symbol}
                                            {excludedText && ` (${excludedText})`}
                                        </div>
                                        <div className="text-xs text-gray-600">{asset.description}</div>
                                    </div>
                                    <div className="text-xs text-gray-600">
                                        {isExcluded && <LuLock className="h-4 w-4" />}
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="p-3">
                            <div className="text-sm text-gray-500">No securities found</div>
                        </div>
                    )}
                </div>
            )}

            {showResults &&
                usePortal &&
                typeof document !== 'undefined' &&
                createPortal(
                    <div
                        className="border-border pointer-events-auto fixed z-[9999] max-h-60 overflow-y-auto rounded-md border bg-white shadow-lg"
                        style={{
                            top: dropdownPosition.top,
                            left: dropdownPosition.left,
                            width: dropdownPosition.width,
                        }}
                        onWheel={(e) => {
                            e.stopPropagation();
                        }}
                    >
                        {isSearching && assets.length === 0 ? (
                            <div className="flex items-center justify-center p-4">
                                <Spinner />
                            </div>
                        ) : assets.length > 0 ? (
                            assets.map((asset) => {
                                const excludedItem = findExcludeItem(asset, excludeItems, allowBlockedSecurities);
                                const isAssetExcluded = excludeAsset?.(asset) ?? false;
                                const isExcluded = !!excludedItem || isAssetExcluded;
                                const excludedText =
                                    excludedItem?.displayText ??
                                    (isAssetExcluded ? excludeAssetDisplayText : undefined);

                                return (
                                    <div
                                        key={asset.sId + '-' + asset.scId}
                                        className={`flex items-center gap-3 border-b border-gray-100 p-3 last:border-b-0 ${
                                            isExcluded
                                                ? 'cursor-not-allowed opacity-60'
                                                : 'cursor-pointer hover:bg-gray-50'
                                        }`}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            if (!isExcluded) {
                                                handleAssetSelect(asset);
                                            }
                                        }}
                                    >
                                        <TickerLogo
                                            tickerSymbol={asset.symbol || ''}
                                            companyName={asset.description || ''}
                                            className="h-8 w-8 rounded object-cover"
                                            fallbackIconClassName="w-8 h-8"
                                        />
                                        <div className="flex-1 text-left">
                                            <div className="text-sm font-medium">
                                                {asset.symbol}
                                                {excludedText && ` (${excludedText})`}
                                            </div>
                                            <div className="text-xs text-gray-600">{asset.description}</div>
                                        </div>
                                        <div className="text-xs text-gray-600">
                                            {isExcluded && <LuLock className="h-4 w-4" />}
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="p-3">
                                <div className="text-sm text-gray-500">No securities found</div>
                            </div>
                        )}
                    </div>,
                    document.body,
                )}
        </div>
    );
};
